import type { ZoneNode } from './zoneUtils';

interface AreaIndex {
  zipToLabel: Map<string, string>;
  labelCount: Map<string, number>;
}

const cache = new WeakMap<ZoneNode[], AreaIndex>();

/**
 * ZIP → district/town label, built from zone_definitions.json exactly like the bot's
 * generated map (GeoData/scripts/generate-zip-name-map.js → Shared/common zip-name-map.ts),
 * so the web and Telegram name areas the same way, e.g. Berlin's "Mitte" → "Berlin Mitte".
 */
function buildIndex(definitions: ZoneNode[]): AreaIndex {
  const cached = cache.get(definitions);
  if (cached) return cached;

  const zipToLabel = new Map<string, string>();
  const traverse = (node: ZoneNode, parentLabel?: string, cityLabel?: string) => {
    const label = node.l || '';
    if (node.t === 'ZI' && parentLabel) {
      const finalLabel =
        cityLabel && parentLabel !== cityLabel && !parentLabel.toLowerCase().includes(cityLabel.toLowerCase())
          ? `${cityLabel} ${parentLabel}`
          : parentLabel;
      zipToLabel.set(label, finalLabel);
    }
    const isNamedParent = node.t === 'DI' || node.t === 'TO' || node.t === 'CI';
    const nextParent = isNamedParent ? label : parentLabel;
    const nextCity = node.t === 'CI' || (node.t === 'TO' && !cityLabel) ? label : cityLabel;
    for (const child of node.c || []) traverse(child, nextParent, nextCity);
  };
  for (const node of definitions) traverse(node);

  const labelCount = new Map<string, number>();
  for (const label of Array.from(zipToLabel.values())) labelCount.set(label, (labelCount.get(label) || 0) + 1);

  const index = { zipToLabel, labelCount };
  cache.set(definitions, index);
  return index;
}

/**
 * Human-readable area text for a set of ZIPs, same rules as the bot's `buildZonesLabel`:
 * grouped by district, "(N PLZ)" when only part of an area is selected, at most 3 groups
 * plus "+N". Returns null until zone definitions are loaded.
 */
export function buildAreaLabel(zips: string[], definitions: ZoneNode[] | null, maxGroups = 3): string | null {
  if (!definitions || zips.length === 0) return null;
  const { zipToLabel, labelCount } = buildIndex(definitions);

  const groups = new Map<string, number>();
  for (const zip of Array.from(new Set(zips))) {
    const label = zipToLabel.get(zip) ?? `PLZ ${zip}`;
    groups.set(label, (groups.get(label) || 0) + 1);
  }

  const parts = Array.from(groups, ([label, selected]) =>
    (labelCount.get(label) ?? 0) > selected ? `${label} (${selected} PLZ)` : label
  );
  const shown = parts.slice(0, maxGroups).join(', ');
  return parts.length > maxGroups ? `${shown} +${parts.length - maxGroups}` : shown;
}
